/**
 * research/researchSnapshot.ts
 *
 * Structured research snapshot / decision packet for MER Algo Footprints
 *
 * Captures a point-in-time institutional-grade decision record:
 *   - full market context
 *   - AI thesis (base + scenarios)
 *   - risk assessment
 *   - supporting evidence
 *
 * Phase 1: localStorage + JSON export
 * Future: PDF export, cloud sync, portfolio-level aggregation
 */

import { now } from '../../../utils/clock';
import { SymbolContext }   from '../../../services/buildSymbolContext';
import { APP_VERSION }    from '../../../config/version';
import { getSessionPhase }  from '../../../utils/session';
import { RiskAssessment, ReadinessTier }  from '../../../risk/riskEngine';
import { ValidatedAIResponse }            from '../../../ai/responseSchema';
import { TradeSetup }                     from '../../../types';
import {
  buildPortfolioState, buildPortfolioSummary,
  PortfolioState, PositionState,
} from '../../../risk/portfolioRisk';
import { assessPositionSize, DEFAULT_RISK_MODEL, RiskModelSettings } from '../../../risk/positionSizing';
import { SIZING_TIER_LABELS } from '../../../risk/positionSizing';

// ─── Snapshot schema ──────────────────────────────────────────────────────────

export interface ResearchSnapshot {
  // Identity
  id:          string;           // UUID-like unique ID
  symbol:      string;
  createdAt:   number;           // Unix ms
  version:     string;           // app version when captured

  // Market state at capture time
  marketState: {
    price:         number;
    vwap:          number | null;
    sessionPhase:  string;
    regimeLabel:   string;
    regimeDominant: string;
    regimeConfidence: number;
    smcBias:       string;
    biasStrength:  string;
    rvol:          number | null;
    vwapDist:      number | null;
    premiumDiscount: string | null;
    dataQuality:   number;        // 0–100 completeness
  };

  // Primary thesis
  thesis: {
    overallBias:    string;
    conviction:     number;
    primaryThesis:  string;
    marketState:    string;
    keyDrivers:     string[];
    whatChanged:    string[];
    uncertainty:    string;
    thesisChange:   string;
  } | null;

  // Scenario framework
  scenarios: {
    base:       ScenarioBlock;
    bullish:    ScenarioBlock;
    bearish:    ScenarioBlock;
    invalidation: string;
  } | null;

  // Best setup + risk
  setup: {
    type:        string;
    dir:         string;
    entry:       string;
    confirmation: string;
    invalidation: string;
    t1:          string;
    t2:          string;
    confidence:  number;
    rationale:   string;
    // v5.5 Patch 10/10: deterministic truth — audit trails must record what system believed
    setupActionable:          boolean;
    maturity:                 string | null;
    alignment:                string | null;
    countertrend:             boolean;
    stateTransitionConfirmed: boolean;
    confirmationMissing:      string[];
  } | null;

  risk: {
    expectedR:       number | null;
    stopQuality:     number;
    targetQuality:   number;
    readiness:       ReadinessTier;
    readinessScore:  number;
    confluenceCount: number;
    trapConflict:    boolean;
    regimeConflict:  boolean;
    uncertaintyNote: string;
  } | null;

  // Supporting evidence
  evidence: {
    smcKeySignal:    string;
    liquidityZones:  number;
    trapCount:       number;
    gammaRegime:     string | null;
    gammaFlip:       number | null;
    hasRealGreeks:   boolean;
  };

  // Notes
  analystNotes: string;         // freeform notes added by trader
  tags:         string[];

  // Decision review (filled in after the fact)
  decisionStatus: DecisionStatus;  // what happened with this decision
  journalEntryId: number | null;   // linked journal entry id (post-trade)

  // Portfolio context at capture time (advisory — based on opportunity board)
  portfolioNote:    string;        // portfolio heat / conflicts at time of capture
  sizingPosture:    string;        // suggested sizing tier at capture time
  concentrationPct: number | null; // concentration score at capture time
}

export type DecisionStatus =
  | 'pending'       // not yet acted on (default)
  | 'acted_on'      // trade was taken
  | 'passed'        // consciously passed
  | 'invalidated'   // thesis was invalidated before action
  | 'worked'        // trade taken and worked (in profit)
  | 'failed'        // trade taken and failed (stopped out)
  | 'unknown';      // outcome unknown

export const DECISION_STATUS_LABELS: Record<DecisionStatus, string> = {
  pending:     '○ Pending',
  acted_on:    '● Acted On',
  passed:      '◎ Passed',
  invalidated: '✕ Invalidated',
  worked:      '✓ Worked',
  failed:      '✗ Failed',
  unknown:     '? Unknown',
};

export const DECISION_STATUS_COLORS: Record<DecisionStatus, string> = {
  pending:     'var(--blue-dim)',
  acted_on:    'var(--cyan)',
  passed:      'var(--blue-muted)',
  invalidated: 'var(--amber)',
  worked:      'var(--green)',
  failed:      'var(--red)',
  unknown:     'var(--t3)',
};

export interface ScenarioBlock {
  label:       string;          // 'Base Case' / 'Bull Scenario' / 'Bear Scenario'
  thesis:      string;          // 1-2 sentence scenario description
  trigger:     string;          // what confirms this path
  target:      string;          // expected outcome / price level
  probability: 'high' | 'medium' | 'low' | 'unknown';
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'mer_research_snapshots_v1';
const MAX_SNAPSHOTS = 100;

// ─── Build snapshot from current context + AI ────────────────────────────────

export function buildResearchSnapshot(
  ctx:           SymbolContext,
  aiResponse:    ValidatedAIResponse | null,
  bestSetup:     TradeSetup | null,
  risk:          RiskAssessment | null,
  analystNotes:  string = '',
  tags:          string[] = [],
  // Optional portfolio context — auto-derived from ctx when absent
  portfolioState?: PortfolioState | null,
  positions?:      PositionState  | null,
  riskModel?:      RiskModelSettings | null
): ResearchSnapshot {
  const ts  = now();
  const id  = `${ctx.symbol}_${ts}_${Math.random().toString(36).slice(2, 6)}`;

  // Market state
  const marketState = {
    price:            ctx.curPrice,
    vwap:             ctx.curVWAP,
    sessionPhase:     getSessionPhase(),
    regimeLabel:      ctx.regime?.label ?? '—',
    regimeDominant:   ctx.regime?.dominant ?? 'unknown',
    regimeConfidence: ctx.regime?.confidence ?? 0,
    smcBias:          ctx.smc?.bias ?? 'neutral',
    biasStrength:     ctx.smc?.biasStrength ?? 'minor',
    rvol:             ctx.regime?.rvol ?? null,
    vwapDist:         ctx.regime?.vwapDist ?? null,
    premiumDiscount:  ctx.smc?.premiumDiscount?.zone ?? null,
    dataQuality:      ctx.dataQuality.completeness,
  };

  // Thesis
  const thesis = aiResponse ? {
    overallBias:    aiResponse.overallBias,
    conviction:     aiResponse.conviction,
    primaryThesis:  aiResponse.primaryThesis,
    marketState:    aiResponse.marketState,
    keyDrivers:     aiResponse.keyDrivers,
    whatChanged:    aiResponse.whatChanged,
    uncertainty:    aiResponse.uncertainty,
    thesisChange:   aiResponse.thesisChange,
  } : null;

  // Build scenario framework from AI response if available
  const scenarios = aiResponse ? buildScenarios(aiResponse, ctx) : null;

  // Setup — use bestActionableSetup for truth, bestSetup for display fields
  const _setupSource = ctx.bestActionableSetup ?? bestSetup;
  const setup = _setupSource ? {
    type:         _setupSource.type,
    dir:          _setupSource.dir,
    entry:        _setupSource.entry,
    confirmation: _setupSource.confirmation,
    invalidation: _setupSource.invalidation,
    t1:           _setupSource.t1,
    t2:           _setupSource.t2,
    confidence:   _setupSource.confidence,
    rationale:    _setupSource.rationale,
    // Deterministic truth — from canonical setupTruth (always from ctx, not AI)
    setupActionable:          ctx.setupActionable,
    maturity:                 ctx.setupTruth.maturity,
    alignment:                ctx.setupTruth.alignment,
    countertrend:             ctx.setupTruth.countertrend,
    stateTransitionConfirmed: ctx.setupTruth.stateTransitionConfirmed,
    confirmationMissing:      ctx.setupTruth.confirmationMissing,
  } : null;

  // Risk
  const riskSnap = risk ? {
    expectedR:       risk.expectedR,
    stopQuality:     risk.stopQuality,
    targetQuality:   risk.targetQuality,
    readiness:       risk.readiness,
    readinessScore:  risk.readinessScore,
    confluenceCount: risk.confluenceCount,
    trapConflict:    risk.trapConflict,
    regimeConflict:  risk.regimeConflict,
    uncertaintyNote: risk.uncertaintyNote,
  } : null;

  // Evidence
  const evidence = {
    smcKeySignal:   ctx.smc?.keySignal ?? 'No dominant SMC signal',
    liquidityZones: ctx.zones.filter(z => z.sig === 'High').length,
    trapCount:      ctx.traps.filter(t => t.severity !== 'None').length,
    gammaRegime:    ctx.gamma?.regime ?? null,
    gammaFlip:      ctx.gamma?.gammaFlip ?? null,
    hasRealGreeks:  ctx.dataQuality.hasGreeks,
  };

  // Auto-derive portfolio context if not supplied by caller
  let _portfolioNote    = '';
  let _sizingPosture    = '';
  let _concentrationPct: number | null = null;

  try {
    // Build a minimal portfolio state from just this symbol's context if none supplied
    const portState = portfolioState ?? buildPortfolioState({ [ctx.symbol]: ctx });
    const portSummary = buildPortfolioSummary(portState);
    _portfolioNote    = portSummary.note !== 'Portfolio balanced.' ? portSummary.note : portSummary.heatLabel;
    _concentrationPct = portState.concentrationScore;

    // Auto-derive sizing posture from best setup + portfolio state
    if (bestSetup && risk) {
      const rm = riskModel ?? DEFAULT_RISK_MODEL;
      const sizing = assessPositionSize(
        bestSetup.entryPrice   ?? null,
        bestSetup.stopPrice    ?? null,
        bestSetup.target1Price ?? null,
        risk.readiness,
        risk.readinessScore,
        bestSetup.confidence,
        portState.heatLevel,
        portState.concentrationScore,
        portState.conflicts.length,
        portState.redundancyGroups.length,
        rm
      );
      _sizingPosture = SIZING_TIER_LABELS[sizing.tier];
    }
  } catch { /* non-critical — snapshot proceeds without portfolio fields */ }

  return {
    id, symbol: ctx.symbol, createdAt: ts, version: APP_VERSION,
    marketState, thesis, scenarios, setup, risk: riskSnap,
    evidence, analystNotes, tags,
    decisionStatus: 'pending',
    journalEntryId: null,
    portfolioNote:    _portfolioNote,
    sizingPosture:    _sizingPosture,
    concentrationPct: _concentrationPct,
  };
}

// ─── Build scenario framework from AI thesis ──────────────────────────────────

function buildScenarios(
  ai:  ValidatedAIResponse,
  ctx: SymbolContext
): ResearchSnapshot['scenarios'] {
  const bias = ai.overallBias;

  // Base case — AI primary thesis
  const base: ScenarioBlock = {
    label:       'Base Case',
    thesis:      ai.primaryThesis,
    trigger:     ai.confirmationNeeded,
    target:      ai.targetLogic,
    probability: ai.conviction >= 70 ? 'high' : ai.conviction >= 50 ? 'medium' : 'low',
  };

  // Bull scenario
  const bullish: ScenarioBlock = {
    label:       'Bull Scenario',
    thesis:      bias === 'bullish'
      ? `Continuation of primary bull thesis — ${ai.targetLogic}`
      : `Bullish reversal possible if ${ai.invalidation} fails to hold`,
    trigger:     bias === 'bullish'
      ? ai.confirmationNeeded
      : `Price reclaims and holds above ${ctx.curVWAP?.toFixed(2) ?? 'VWAP'}`,
    target:      ai.targetLogic,
    probability: bias === 'bullish' ? 'high' : bias === 'neutral' ? 'medium' : 'low',
  };

  // Bear scenario
  const bearish: ScenarioBlock = {
    label:       'Bear Scenario',
    thesis:      bias === 'bearish'
      ? `Continuation of primary bear thesis — ${ai.primaryRisk}`
      : `Bearish deterioration if ${ai.confirmationNeeded} fails`,
    trigger:     bias === 'bearish'
      ? ai.confirmationNeeded
      : `Price breaks and holds below ${ctx.curVWAP?.toFixed(2) ?? 'VWAP'}`,
    target:      `${ai.primaryRisk} materializes`,
    probability: bias === 'bearish' ? 'high' : bias === 'neutral' ? 'medium' : 'low',
  };

  return {
    base, bullish, bearish,
    invalidation: ai.invalidation,
  };
}

// ─── Storage operations ───────────────────────────────────────────────────────

export function saveSnapshot(snapshot: ResearchSnapshot): boolean {
  try {
    const existing = loadAllSnapshots();
    const updated  = [snapshot, ...existing].slice(0, MAX_SNAPSHOTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
}

export function loadAllSnapshots(): ResearchSnapshot[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as ResearchSnapshot[];
  } catch {
    return [];
  }
}

export function loadSnapshotsForSymbol(symbol: string): ResearchSnapshot[] {
  return loadAllSnapshots().filter(s => s.symbol === symbol);
}

export function deleteSnapshot(id: string): void {
  try {
    const updated = loadAllSnapshots().filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch {}
}

export function updateDecisionStatus(
  id:     string,
  status: DecisionStatus,
  journalEntryId?: number
): boolean {
  try {
    const all = loadAllSnapshots();
    const idx = all.findIndex(s => s.id === id);
    if (idx < 0) return false;
    all[idx].decisionStatus = status;
    if (journalEntryId !== undefined) all[idx].journalEntryId = journalEntryId;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    return true;
  } catch { return false; }
}

export function loadSnapshotsByStatus(status: DecisionStatus): ResearchSnapshot[] {
  return loadAllSnapshots().filter(s => s.decisionStatus === status);
}

export function loadSnapshotsBySetupType(setupType: string): ResearchSnapshot[] {
  return loadAllSnapshots().filter(s => s.setup?.type === setupType);
}

export function loadSnapshotsByReadiness(readiness: string): ResearchSnapshot[] {
  return loadAllSnapshots().filter(s => s.risk?.readiness === readiness);
}

// ─── Server persistence (decision_snapshots via SQLite) ──────────────────────
// Called from the explicit "Save Snapshot" action in ThesisPanel.
// Lightweight in-memory dedupe prevents double-posting within the same session.

const _recentPosts = new Map<string, number>(); // key → last post time
const _DEDUPE_MS = 60_000;                       // suppress duplicate within 60 s

/**
 * Builds a stable context fingerprint from canonical fields available on any snapshot.
 * Used as context_hash when no AI inputHash is available.
 * Same decision state → same fingerprint.
 * Timestamp is intentionally excluded to keep hash stable across re-captures.
 */
function buildContextFingerprint(snapshot: ResearchSnapshot): string {
  // Fixed field order — never reorder or dedupe keys will diverge across versions.
  // Timestamp is excluded so same decision state always produces same fingerprint.
  const parts = [
    snapshot.symbol,
    snapshot.thesis?.overallBias ?? 'null',
    String(snapshot.thesis?.conviction ?? 'null'),
    snapshot.risk?.readiness     ?? 'null',
    String(snapshot.risk?.readinessScore ?? 'null'),
    snapshot.setup?.type ?? 'null',
    snapshot.setup?.dir  ?? 'null',
    snapshot.risk?.expectedR != null ? snapshot.risk.expectedR.toFixed(2) : 'null',
  ];
  return parts.join('|');
}

function buildStableContextHash(snap: ResearchSnapshot): string | null {
  try {
    return JSON.stringify({
      s:  snap.symbol                 ?? null,
      b:  snap.thesis?.overallBias    ?? null,
      c:  snap.thesis?.conviction     ?? null,
      r:  snap.risk?.readiness        ?? null,
      rs: snap.risk?.readinessScore   ?? null,
      er: snap.risk?.expectedR        ?? null,
      st: snap.setup?.type            ?? null,
      sd: snap.setup?.dir             ?? null,
    });
  } catch {
    return null;
  }
}

/**
 * ONE canonical orchestration function for capturing and persisting a decision snapshot.
 *
 * This is the ONLY function components should call for a full capture.
 *   1. Saves to localStorage (existing behavior, unchanged)
 *   2. Posts to server SQLite via postDecisionSnapshot()
 *
 * All decision snapshot server writes go through this function.
 * captureSnapshot in ThesisPanel calls only this — not saveSnapshot or
 * postDecisionSnapshot independently.
 */
export async function captureAndPersistDecisionSnapshot(
  snapshot: ResearchSnapshot,
  inputHash?: string | null   // pass cur.inputHash from ThesisPanel if available
): Promise<void> {
  // Attach inputHash so postDecisionSnapshot can include it as context_hash
  (snapshot as ResearchSnapshot & { _inputHash?: string })._inputHash = inputHash ?? undefined;
  saveSnapshot(snapshot);                   // localStorage — always
  // Ensure stable context_hash and decision_taken before server write
  (snapshot as ResearchSnapshot & { context_hash?: string | null }).context_hash =
    buildStableContextHash(snapshot);
  (snapshot as ResearchSnapshot & { decision_taken?: string }).decision_taken =
    (snapshot as ResearchSnapshot & { decision_taken?: string }).decision_taken ?? 'captured';
  await postDecisionSnapshot(snapshot);     // server SQLite — fire-and-forget
}

export async function postDecisionSnapshot(snapshot: ResearchSnapshot): Promise<boolean> {
  // Compute context_hash first — used for both dedupe key and payload.
  // Prefer the AI response's inputHash (canonical engine-input fingerprint);
  // fall back to our stable field fingerprint when no thesis has run yet.
  const context_hash: string =
    (snapshot as ResearchSnapshot & { _inputHash?: string })._inputHash
    || buildContextFingerprint(snapshot);

  // decision_taken is always 'captured' at this trigger — the snapshot is being
  // saved by explicit user action regardless of snapshot.decisionStatus ('pending').
  const decision_taken = 'captured';

  // Dedupe key uses all three discriminating fields so the guard is tight.
  const dedupeKey = `${snapshot.symbol}:${context_hash}:${decision_taken}`;
  const last = _recentPosts.get(dedupeKey);
  if (last && (Date.now() - last) < _DEDUPE_MS) {
    console.debug(`[Decision] Dedupe suppressed: ${snapshot.symbol} (within ${_DEDUPE_MS/1000}s window)`);
    return false;
  }
  _recentPosts.set(dedupeKey, Date.now());

  // Map ResearchSnapshot fields to the decision_snapshots table columns.
  const payload = {
    timestamp:      snapshot.createdAt,
    ticker:         snapshot.symbol,
    context_hash,
    ai_bias:        snapshot.thesis?.overallBias ?? null,
    ai_conviction:  snapshot.thesis?.conviction  != null
                      ? String(snapshot.thesis.conviction)
                      : null,
    readiness:      snapshot.risk?.readinessScore ?? null,
    expected_r:     snapshot.risk?.expectedR      ?? null,
    sizing_tier:    snapshot.sizingPosture        || null,
    portfolio_heat: snapshot.concentrationPct     ?? null,
    decision_taken,
  };

  try {
    // Detect server origin (same as scanner bridge)
    const origin = typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host}`
      : 'http://localhost:3000';

    const res = await fetch(`${origin}/api/learning/snapshot`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });
    return res.ok;
  } catch {
    // Server not running — silent fail, localStorage copy already saved
    return false;
  }
}

export function exportSnapshotAsJSON(snapshot: ResearchSnapshot): void {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `MER_${snapshot.symbol}_${new Date(snapshot.createdAt).toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportSnapshotsAsCSV(snapshots: ResearchSnapshot[]): void {
  const headers = [
    'Date', 'Symbol', 'Bias', 'Conviction', 'Regime', 'Readiness',
    'ExpectedR', 'SetupType', 'Confluence', 'DataQuality',
    'DecisionStatus', 'SizingPosture', 'Tags', 'Notes'
  ];
  const rows = snapshots.map(s => [
    new Date(s.createdAt).toISOString(),
    s.symbol,
    s.thesis?.overallBias ?? '—',
    s.thesis?.conviction ?? '—',
    s.marketState.regimeLabel,
    s.risk?.readiness ?? '—',
    s.risk?.expectedR?.toFixed(2) ?? '—',
    s.setup?.type ?? '—',
    s.risk?.confluenceCount ?? '—',
    s.marketState.dataQuality,
    s.decisionStatus ?? 'pending',
    s.sizingPosture ?? '—',
    s.tags.join(';'),
    `"${s.analystNotes.replace(/"/g, '""')}"`,
  ]);

  const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `MER_Research_Snapshots_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// getSessionPhase() imported from utils/session.ts
